import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const TimelineContainer = styled.div`
  --bg: #0b0d12;
  --panel: #121623;
  --panel-2: #0f1320;
  --text: #e8ecff;
  --muted: #aab2d6;
  --grid: rgba(255,255,255,.06);
  --grid-strong: rgba(255,255,255,.12);
  --playhead: #ff4d6d;
  --block: rgba(122, 162, 255, .18);
  --block-border: rgba(122, 162, 255, .55);
  --row-h: 44px;
  --ruler-h: 56px;
  --left-col: 160px;
  --radius: 12px;
  --shadow: 0 10px 30px rgba(0,0,0,.35);
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;

  background: radial-gradient(1200px 600px at 10% -10%, rgba(122,162,255,.25), transparent 60%),
              radial-gradient(1200px 600px at 90% -20%, rgba(255,77,109,.18), transparent 60%),
              var(--bg);
  color: var(--text);
  padding: 20px;
  border-radius: var(--radius);
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  h1 {
    font-size: 18px;
    margin: 0;
    letter-spacing: 0.2px;
  }

  .sub {
    font-size: 12px;
    color: var(--muted);
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Pill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.10);
  border-radius: 999px;
  padding: 8px 10px;
  box-shadow: var(--shadow);
  backdrop-filter: blur(6px);

  label {
    font-size: 12px;
    color: var(--muted);
    user-select: none;
    white-space: nowrap;
  }

  input[type='range'] {
    width: 160px;
  }

  button {
    background: rgba(255,255,255,.08);
    color: var(--text);
    border: 1px solid rgba(255,255,255,.12);
    border-radius: 10px;
    padding: 6px 10px;
    cursor: pointer;

    &:hover {
      border-color: rgba(255,255,255,.22);
    }
  }
`;

const TimelineShell = styled.div`
  background: linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.03));
  border: 1px solid rgba(255,255,255,.10);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;

  &.readonly .gridBg {
    display: none;
  }

  &.readonly .handle {
    display: none;
  }
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: var(--left-col) 1fr;
  background: rgba(0,0,0,.12);
  border-bottom: 1px solid rgba(255,255,255,.08);
`;

const HeaderLabel = styled.div`
  padding: 14px 12px;
  font-size: 12px;
  color: var(--muted);
  border-right: 1px solid rgba(255,255,255,.08);
`;

const ScrollArea = styled.div`
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  height: calc(var(--ruler-h) + 6px);
  background: var(--panel-2);
`;

const Ruler = styled.div`
  position: relative;
  height: var(--ruler-h);
  min-height: var(--ruler-h);
`;

const GridBg = styled.div`
  position: absolute;
  inset: 0;
  background: ${props => props.strong ? 
    'repeating-linear-gradient(to right, var(--grid-strong) 0px, var(--grid-strong) 1px, transparent 1px, transparent var(--major-grid))' :
    'repeating-linear-gradient(to right, var(--grid) 0px, var(--grid) 1px, transparent 1px, transparent var(--minor-grid))'};
  pointer-events: none;
  opacity: ${props => props.strong ? 0.9 : 1};
  mix-blend-mode: ${props => props.strong ? 'screen' : 'normal'};
`;

const Playhead = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--playhead);
  box-shadow: 0 0 0 1px rgba(255,77,109,.25), 0 0 18px rgba(255,77,109,.35);
  pointer-events: none;
  z-index: 50;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: var(--left-col) 1fr;
  background: var(--panel);
`;

const Labels = styled.div`
  border-right: 1px solid rgba(255,255,255,.08);
`;

const Label = styled.div`
  height: var(--row-h);
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-bottom: 1px solid rgba(255,255,255,.06);
  color: var(--muted);
  font-size: 12px;
  gap: 8px;
`;

const Badge = styled.span`
  font-size: 10px;
  padding: 3px 7px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.14);
  background: rgba(255,255,255,.06);
  color: var(--text);
  min-width: 18px;
  display: inline-flex;
  justify-content: center;
`;

const RowsScroll = styled.div`
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  background: var(--panel-2);
`;

const Rows = styled.div`
  position: relative;
`;

const Row = styled.div`
  position: relative;
  height: var(--row-h);
  border-bottom: 1px solid rgba(255,255,255,.06);

  .gridBg {
    opacity: 0.7;
  }

  .gridBg.strong {
    opacity: 0.35;
  }
`;

const Block = styled.div`
  position: absolute;
  top: 8px;
  height: calc(var(--row-h) - 16px);
  border-radius: 10px;
  background: var(--block);
  border: 1px solid var(--block-border);
  display: flex;
  align-items: center;
  padding: 0 10px;
  gap: 8px;
  user-select: none;
  backdrop-filter: blur(4px);
  white-space: nowrap;
  box-sizing: border-box;
  cursor: ${props => props.editing ? 'grab' : 'default'};

  .name {
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.2px;
  }

  .meta {
    font-size: 11px;
    color: var(--muted);
  }

  &:active {
    cursor: grabbing;
  }
`;

const Handle = styled.div`
  position: absolute;
  top: 2px;
  bottom: 2px;
  width: 10px;
  border-radius: 8px;
  background: rgba(255,255,255,.10);
  border: 1px solid rgba(255,255,255,.14);
  opacity: 0.95;
  cursor: ${props => props.side === 'left' ? 'ew-resize' : 'ew-resize'};
  left: ${props => props.side === 'left' ? '2px' : 'auto'};
  right: ${props => props.side === 'right' ? '2px' : 'auto'};
`;

const FooterHint = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: var(--muted);
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const Kbd = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,.14);
  background: rgba(255,255,255,.06);
  color: var(--text);
`;

/**
 * TimelineComponent - Reusable timeline with action blocks
 * 
 * @param {Object} props
 * @param {number} props.durationSec - Total duration in seconds
 * @param {Array} props.actions - Array of action objects: {id, type, name, start, end}
 * @param {boolean} props.readOnly - Disable editing (default: false)
 * @param {Function} props.onActionChange - Callback when action is modified
 * @param {string} props.title - Timeline title (default: "Video Timeline")
 * @param {number} props.currentTime - External current time (for sync with video)
 * @param {boolean} props.playing - External playing state (for sync with video)
 * @param {Function} props.onPlayingChange - Callback when play/pause changes
 * @param {Function} props.onTimeChange - Callback when time changes (seeking)
 */
const TimelineComponent = ({
  durationSec = 180,
  actions = [],
  readOnly = false,
  onActionChange = null,
  title = 'Video Timeline',
  currentTime: externalCurrentTime = null,
  playing: externalPlaying = null,
  onPlayingChange = null,
  onTimeChange = null
}) => {
  const [state, setState] = useState({
    pxPerSec: 8,
    minDurationSec: 0.2,
    snapSec: 0.1
  });

  const [internalCurrentTime, setInternalCurrentTime] = useState(0);
  const [internalPlaying, setInternalPlaying] = useState(false);
  const [localActions, setLocalActions] = useState(actions);
  const rafRef = useRef(null);
  const rulerScrollRef = useRef(null);
  const rowsScrollRef = useRef(null);
  const rulerRef = useRef(null);
  const rowsRef = useRef(null);
  const labelsRef = useRef(null);
  const playheadRef = useRef(null);
  const rulerMinorRef = useRef(null);
  const rulerMajorRef = useRef(null);
  const syncFromExternalRef = useRef(false);

  // Use external time/playing if provided, otherwise use internal state
  const currentTime = externalCurrentTime !== null ? externalCurrentTime : internalCurrentTime;
  const playing = externalPlaying !== null ? externalPlaying : internalPlaying;

  const handlePlayingChange = (newPlaying) => {
    if (externalPlaying === null) {
      setInternalPlaying(newPlaying);
    }
    if (onPlayingChange && !syncFromExternalRef.current) {
      onPlayingChange(newPlaying);
    }
  };

  const handleTimeChange = (newTime) => {
    if (externalCurrentTime === null) {
      setInternalCurrentTime(newTime);
    }
    if (onTimeChange && !syncFromExternalRef.current) {
      onTimeChange(newTime);
    }
  };

  useEffect(() => {
    setLocalActions(actions);
  }, [actions]);

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const snap = (sec) => {
    if (!state.snapSec || state.snapSec <= 0) return sec;
    return Math.round(sec / state.snapSec) * state.snapSec;
  };

  const formatTime = (sec) => {
    const s = Math.max(0, sec);
    const m = Math.floor(s / 60);
    const r = s - m * 60;
    const ss = Math.floor(r);
    const tenth = Math.floor((r - ss) * 10);
    return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}.${tenth}`;
  };

  const totalWidthPx = () => Math.ceil(durationSec * state.pxPerSec) + 40;

  const secToX = (sec) => sec * state.pxPerSec;
  const xToSec = (x) => x / state.pxPerSec;

  const buildTracksFromActions = () => {
    const map = new Map();
    for (const a of localActions) {
      if (!map.has(a.type)) map.set(a.type, []);
      map.get(a.type).push(a);
    }
    const types = [...map.keys()].sort((x, y) => x.localeCompare(y));
    return types.map(type => {
      const items = map.get(type).sort((x, y) => x.start - y.start);
      return {
        id: type,
        badge: items[0]?.name || 'Action',
        label: type,
        items: items
      };
    });
  };

  const setZoom = (sliderVal) => {
    const pxPerSec = sliderVal / 10;
    setState(prev => ({ ...prev, pxPerSec }));
  };

  const fitAllTracks = () => {
    // Calculate zoom to fit entire timeline on screen without scrolling
    if (rowsScrollRef.current) {
      const availableWidth = rowsScrollRef.current.clientWidth;
      const optimalPxPerSec = Math.max(0.5, availableWidth / durationSec);
      setState(prev => ({ ...prev, pxPerSec: optimalPxPerSec }));
    }
  };

  const renderLayout = () => {
    const w = totalWidthPx();
    if (rulerRef.current) rulerRef.current.style.width = w + 'px';
    if (rowsRef.current) rowsRef.current.style.width = w + 'px';

    let minorSec = 1;
    let majorSec = 5;
    if (state.pxPerSec < 6) {
      minorSec = 2;
      majorSec = 10;
    }
    if (state.pxPerSec < 3) {
      minorSec = 5;
      majorSec = 20;
    }

    const minorPx = minorSec * state.pxPerSec;
    const majorPx = majorSec * state.pxPerSec;

    if (rulerMinorRef.current) rulerMinorRef.current.style.setProperty('--minor-grid', minorPx + 'px');
    if (rulerMajorRef.current) rulerMajorRef.current.style.setProperty('--major-grid', majorPx + 'px');
  };

  const renderRulerTicks = () => {
    if (!rulerRef.current) return;

    // Remove old ticks
    rulerRef.current.querySelectorAll('.tick, .tickLabel').forEach(n => n.remove());

    let labelEverySec = 5;
    if (state.pxPerSec >= 10) labelEverySec = 2;
    if (state.pxPerSec >= 16) labelEverySec = 1;
    if (state.pxPerSec < 6) labelEverySec = 10;

    for (let t = 0; t <= durationSec; t += labelEverySec) {
      const x = Math.round(t * state.pxPerSec);

      const tick = document.createElement('div');
      tick.className = 'tick';
      tick.style.left = x + 'px';
      tick.style.position = 'absolute';
      tick.style.top = '8px';
      tick.style.height = '16px';
      tick.style.width = '1px';
      tick.style.background = 'rgba(255,255,255,.22)';
      rulerRef.current.appendChild(tick);

      // Only show time labels when zoomed in past 30 (pxPerSec > 3)
      if (state.pxPerSec > 3) {
        const label = document.createElement('div');
        label.className = 'tickLabel';
        label.style.position = 'absolute';
        label.style.top = '26px';
        label.style.left = x + 'px';
        label.style.transform = 'translateX(-50%)';
        label.style.fontSize = '11px';
        label.style.color = 'var(--muted)';
        label.style.userSelect = 'none';
        label.style.whiteSpace = 'nowrap';
        label.textContent = formatTime(t).replace('.0', '');
        rulerRef.current.appendChild(label);
      }
    }
  };

  const syncScroll = (from, to) => {
    if (to) to.scrollLeft = from.scrollLeft;
  };

  const clickSeek = (e, scrollEl) => {
    if (e.target.closest('.block')) return;
    const rect = scrollEl.getBoundingClientRect();
    const xInViewport = e.clientX - rect.left;
    const x = xInViewport + scrollEl.scrollLeft;
    handleTimeChange(clamp(xToSec(x), 0, durationSec));
  };

  const renderPlayhead = () => {
    if (playheadRef.current) {
      playheadRef.current.style.left = secToX(currentTime) + 'px';
    }
  };

  const findItem = (itemId) => localActions.find(a => a.id === itemId);

  const updateAction = (itemId, updates) => {
    const newActions = localActions.map(a => a.id === itemId ? { ...a, ...updates } : a);
    setLocalActions(newActions);
    if (onActionChange) onActionChange(newActions);
  };

  // Effects
  useEffect(() => {
    renderLayout();
    renderRulerTicks();
    renderPlayhead();
  }, [state.pxPerSec, durationSec]);

  useEffect(() => {
    renderPlayhead();
  }, [currentTime]);

  useEffect(() => {
    if (externalCurrentTime !== null) {
      syncFromExternalRef.current = true;
      setTimeout(() => {
        syncFromExternalRef.current = false;
      }, 50);
    }
  }, [externalCurrentTime]);

  useEffect(() => {
    if (externalPlaying !== null) {
      syncFromExternalRef.current = true;
      setTimeout(() => {
        syncFromExternalRef.current = false;
      }, 50);
    }
  }, [externalPlaying]);

  useEffect(() => {
    const handleRulerScroll = () => syncScroll(rulerScrollRef.current, rowsScrollRef.current);
    const handleRowsScroll = () => syncScroll(rowsScrollRef.current, rulerScrollRef.current);

    if (rulerScrollRef.current) rulerScrollRef.current.addEventListener('scroll', handleRulerScroll);
    if (rowsScrollRef.current) rowsScrollRef.current.addEventListener('scroll', handleRowsScroll);

    return () => {
      if (rulerScrollRef.current) rulerScrollRef.current.removeEventListener('scroll', handleRulerScroll);
      if (rowsScrollRef.current) rowsScrollRef.current.removeEventListener('scroll', handleRowsScroll);
    };
  }, []);

  // Play/pause animation loop (only run if internally controlled)
  useEffect(() => {
    // Only animate if we're NOT externally controlled
    // If externalPlaying is provided, the external source (video) controls timing
    if (!playing || externalPlaying !== null) return;

    const tick = () => {
      setInternalCurrentTime(prev => {
        const newTime = prev + 1 / 60;
        if (newTime >= durationSec) {
          handlePlayingChange(false);
          if (onTimeChange && !syncFromExternalRef.current) onTimeChange(durationSec);
          return durationSec;
        }
        if (onTimeChange && !syncFromExternalRef.current) onTimeChange(newTime);
        return newTime;
      });
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, durationSec, externalPlaying]);

  const tracks = buildTracksFromActions();

  return (
    <TimelineContainer>
      <TopBar>
        <Title>
          <h1>{title}</h1>
          <div className="sub">Edit actions by dragging and resizing blocks</div>
        </Title>

        <Controls>
          <Pill>
            <label>Zoom</label>
            <input
              type="range"
              min="20"
              max="240"
              value={state.pxPerSec * 10}
              onChange={e => setState(prev => ({ ...prev, pxPerSec: Number(e.target.value) / 10 }))}
            />
            <span style={{ width: '44px', fontSize: '12px', color: 'var(--muted)' }}>
              {(state.pxPerSec * 10).toFixed(0)}
            </span>
            <button onClick={fitAllTracks} style={{ fontSize: '11px' }}>Fit All</button>
          </Pill>

          <Pill>
            <label>Time</label>
            <span style={{ fontSize: '12px', color: 'var(--text)', minWidth: '92px' }}>
              {formatTime(currentTime)} - 
              {Math.round(currentTime)}s
            </span>
            <button onClick={() => handlePlayingChange(!playing)}>
              {playing ? 'Pause' : 'Play'}
            </button>
          </Pill>
        </Controls>
      </TopBar>

      <TimelineShell className={readOnly ? 'readonly' : 'edit'}>
        <HeaderRow>
          <HeaderLabel>Timeline</HeaderLabel>
          <ScrollArea ref={rulerScrollRef}>
            <Ruler ref={rulerRef}>
              <GridBg ref={rulerMinorRef} />
              <GridBg ref={rulerMajorRef} strong />
            </Ruler>
          </ScrollArea>
        </HeaderRow>

        <Body>
          <Labels ref={labelsRef}>
            {tracks.map(track => (
              <Label key={`label-${track.id}`}>
                <span>{track.badge}</span>
              </Label>
            ))}
          </Labels>

          <RowsScroll ref={rowsScrollRef} onMouseDown={e => clickSeek(e, rowsScrollRef.current)}>
            <Rows ref={rowsRef}>
              {tracks.map(track => (
                <Row key={`row-${track.id}`} data-track-id={track.id}>
                  <GridBg />
                  <GridBg strong />
                  {track.items.map(item => (
                    <BlockRenderer
                      key={`block-${item.id}`}
                      item={item}
                      secToX={secToX}
                      xToSec={xToSec}
                      formatTime={formatTime}
                      snap={snap}
                      state={state}
                      readOnly={readOnly}
                      durationSec={durationSec}
                      onUpdate={updateAction}
                    />
                  ))}
                </Row>
              ))}
            </Rows>
            <Playhead ref={playheadRef} />
          </RowsScroll>
        </Body>
      </TimelineShell>

      <FooterHint>
        <span>
          <Kbd>Click</Kbd> timeline to seek
        </span>
        {!readOnly && (
          <>
            <span>
              <Kbd>Drag</Kbd> block to move
            </span>
            <span>
              <Kbd>Drag</Kbd> ends to resize
            </span>
          </>
        )}
      </FooterHint>
    </TimelineContainer>
  );
};

/**
 * BlockRenderer - Individual block component for actions
 */
const BlockRenderer = ({
  item,
  secToX,
  xToSec,
  formatTime,
  snap,
  state,
  readOnly,
  durationSec,
  onUpdate
}) => {
  const blockRef = useRef(null);
  const [mode, setMode] = useState(null); // "move" | "resize-left" | "resize-right"
  const startXRef = useRef(0);
  const startStartSecRef = useRef(0);
  const startEndSecRef = useRef(0);

  const handleMouseDown = (e, handleType) => {
    if (readOnly) return;

    e.preventDefault();
    startXRef.current = e.clientX;
    startStartSecRef.current = item.start;
    startEndSecRef.current = item.end;

    if (handleType === 'left') {
      setMode('resize-left');
    } else if (handleType === 'right') {
      setMode('resize-right');
    } else {
      setMode('move');
    }
  };

  useEffect(() => {
    if (!mode) return;

    const handleMouseMove = (e) => {
      const dx = e.clientX - startXRef.current;
      const deltaSecRaw = dx / state.pxPerSec;
      const deltaSec = snap(deltaSecRaw);

      if (mode === 'move') {
        const newStart = snap(clamp(startStartSecRef.current + deltaSec, 0, durationSec));
        const duration = startEndSecRef.current - startStartSecRef.current;
        const newEnd = clamp(newStart + duration, 0, durationSec);
        onUpdate(item.id, { start: newStart, end: newEnd });
      } else if (mode === 'resize-left') {
        const newStart = snap(clamp(startStartSecRef.current + deltaSec, 0, startEndSecRef.current - 0.2));
        onUpdate(item.id, { start: newStart });
      } else if (mode === 'resize-right') {
        const newEnd = snap(clamp(startEndSecRef.current + deltaSec, startStartSecRef.current + 0.2, durationSec));
        onUpdate(item.id, { end: newEnd });
      }
    };

    const handleMouseUp = () => {
      setMode(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [mode, state.pxPerSec, durationSec, snap, item.id, onUpdate, item.start, item.end]);

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const left = secToX(item.start);
  const width = Math.max(8, secToX(item.end - item.start));

  return (
    <Block
      ref={blockRef}
      data-item-id={item.id}
      style={{ left: left + 'px', width: width + 'px' }}
      editing={!readOnly}
      onMouseDown={e => handleMouseDown(e, null)}
    >
      {!readOnly && (
        <>
          <Handle side="left" onMouseDown={e => handleMouseDown(e, 'left')} />
          <Handle side="right" onMouseDown={e => handleMouseDown(e, 'right')} />
        </>
      )}
      <div className="name">{item.name}</div>
      <div className="meta">
        {formatTime(item.start)} → {formatTime(item.end)}
      </div>
    </Block>
  );
};

export default TimelineComponent;
